import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import { voiceApi, errorMessage } from './voiceApi';
import { formatPhone, teacherDialNumber } from './callFormat';

const STUDENT_NUMBERS = [
    { field: 'phone', label: 'Student', contact_label: 'student' },
    { field: 'parent_phone', label: 'Parent', contact_label: 'parent' },
    { field: 'parent_phone_2', label: 'Parent 2', contact_label: 'parent_2' },
];

const GroupLabel = ({ children }) => (
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, fontWeight: 600, letterSpacing: 0.5 }}>{children}</Typography>
);

/**
 * Students (name or consultation #) and teachers (name or email) in one search (plan §5.11).
 *   mode="call"   → Call buttons: Student / Parent / Parent 2 for a student, one for a teacher (US / Canada only);
 *                   onPick({ student, number, contact_label }) or onPick({ teacher, number, contact_label: 'teacher' })
 *   mode="attach" → one Attach button per person; onPick({ student }) or onPick({ teacher })
 * "Also file under" keeps the students-only StudentSearch.
 */
const ContactSearch = ({ mode = 'call', onPick, autoFocus = false, placeholder }) => {
    const [q, setQ] = useState('');
    const [students, setStudents] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [partial, setPartial] = useState(null);   // one half failed: 'students' | 'teachers' | null
    const timer = useRef(null);
    const seq = useRef(0);

    useEffect(() => {
        if (timer.current) clearTimeout(timer.current);
        const term = q.trim();
        if (term.length < 2) { setStudents([]); setTeachers([]); setError(null); setPartial(null); setLoading(false); return undefined; }
        setLoading(true);
        const mySeq = ++seq.current;
        timer.current = setTimeout(async () => {
            // The two halves are independent: one failing must not hide the results the other returned.
            const [s, t] = await Promise.all([
                voiceApi.searchStudents(term, 8).then((d) => d, (err) => err),
                voiceApi.searchTeachers(term, 6).then((d) => d, (err) => err),
            ]);
            if (mySeq !== seq.current) return;
            const studentsFailed = s instanceof Error;
            const teachersFailed = t instanceof Error;
            setStudents(studentsFailed ? [] : (s.students || []));
            setTeachers(teachersFailed ? [] : (t.teachers || []));
            setError(studentsFailed && teachersFailed ? errorMessage(s) : null);
            setPartial(studentsFailed && !teachersFailed ? 'students' : teachersFailed && !studentsFailed ? 'teachers' : null);
            setLoading(false);
        }, 300);
        return () => clearTimeout(timer.current);
    }, [q]);

    const term = q.trim();
    const nothing = !loading && term.length >= 2 && !students.length && !teachers.length && !error && !partial;

    return (
        <Box>
            <TextField
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={placeholder || 'Student or teacher name, or consultation #'}
                size="small"
                fullWidth
                autoFocus={autoFocus}
                InputProps={{ endAdornment: loading ? <CircularProgress size={14} /> : null }}
            />
            {error && <Typography variant="caption" color="error">{error}</Typography>}
            {partial && (
                <Typography variant="caption" color="warning.main" display="block">
                    {partial === 'teachers' ? 'Teachers could not be searched just now; students are shown.' : 'Students could not be searched just now; teachers are shown.'}
                </Typography>
            )}
            {(students.length > 0 || teachers.length > 0) && (
                <Box sx={{ mt: 0.5, maxHeight: 260, overflowY: 'auto' }}>
                    {students.length > 0 && <GroupLabel>STUDENTS</GroupLabel>}
                    {students.map((s) => {
                        const numbers = STUDENT_NUMBERS.filter((n) => s[n.field]);
                        return (
                            <Box key={`student-${s.uuid}`} sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {s.first_name} {s.last_name}
                                    {s.korean_name ? <Typography component="span" variant="caption" color="text.secondary"> {s.korean_name}</Typography> : null}
                                    {s.consultation_number ? <Typography component="span" variant="caption" color="text.secondary"> · #{s.consultation_number}</Typography> : null}
                                    {s.is_potential_student ? <Typography component="span" variant="caption" color="warning.main"> · potential</Typography> : null}
                                </Typography>
                                {mode === 'attach' ? (
                                    <Button size="small" onClick={() => onPick({ student: s })}>Attach</Button>
                                ) : numbers.length === 0 ? (
                                    <Typography variant="caption" color="text.secondary">No phone numbers on file</Typography>
                                ) : (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {numbers.map((n) => (
                                            <Button
                                                key={n.field}
                                                size="small"
                                                variant="outlined"
                                                startIcon={<PhoneOutlinedIcon sx={{ fontSize: 14 }} />}
                                                onClick={() => onPick({ student: s, number: s[n.field], contact_label: n.contact_label })}
                                                sx={{ textTransform: 'none', py: 0 }}
                                            >
                                                {n.label} {formatPhone(s[n.field])}
                                            </Button>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                    {teachers.length > 0 && <GroupLabel>TEACHERS</GroupLabel>}
                    {teachers.map((t) => {
                        const dial = t.e164 || teacherDialNumber(t);
                        return (
                            <Box key={`teacher-${t.uuid}`} sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {t.first_name} {t.last_name}
                                    {t.subject ? <Typography component="span" variant="caption" color="text.secondary"> · {String(t.subject).split('|').join(', ')}</Typography> : null}
                                    {/* no login email = interviewed, never hired; the server sorts these last */}
                                    {t.is_potential ? <Typography component="span" variant="caption" color="warning.main"> · potential</Typography> : null}
                                </Typography>
                                {mode === 'attach' ? (
                                    <Button size="small" onClick={() => onPick({ teacher: t })}>Attach</Button>
                                ) : dial ? (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<PhoneOutlinedIcon sx={{ fontSize: 14 }} />}
                                        onClick={() => onPick({ teacher: t, number: dial, contact_label: 'teacher' })}
                                        sx={{ textTransform: 'none', py: 0 }}
                                    >
                                        Teacher {formatPhone(dial)}
                                    </Button>
                                ) : (
                                    <Typography variant="caption" color="text.secondary">
                                        {t.phone ? 'Only US and Canada numbers can be called' : 'No phone number on file'}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            )}
            {nothing && <Typography variant="caption" color="text.secondary">No students or teachers found.</Typography>}
        </Box>
    );
};

export default ContactSearch;
